/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterCheckIcon } from "@/components/icons/FilterCheckIcon";
import { FilterIcon } from "@/components/icons/FilterIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from '@/utils/dateUtils';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, X, Info, Download } from "lucide-react";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import CallDetailsModal from "./CallDetailsModal";
import Timeline from "./Timeline";
import * as XLSX from 'xlsx';
import { PaginatedResponse } from "@/lib/axios/types";
import {
  CallDetailOverviewRow,
  CallOverviewColumnFilterItem,
  FilterDiagnosticsMetric,
  FilterRequestState,
  InsightSection,
} from "@/types/call-detail-overview";
import { fetchCallDetailOverviewColumnFilter, fetchCallDetailOverviewPaged } from "@/lib/axios/api/call-detail-overview.api";
import { CallDirectionTypes } from "@/enums/call-direction";
import { CallsOverviewTableColumns } from "@/enums/call-overvew-column";
import { AgentQueueBadge } from "@/components/widgets/agents/components/TotalHandledCallsModal";
import { Checkbox } from "@/components/ui/checkbox";
import { filterRowsWithValidStartTime } from "@/utils/callJourneyTimeFilter";
import {
  formatStartTimeDisplayValue,
  normalizeColumnValues,
} from "@/utils/callJourneyWidgetFilters";

export interface CallsDetailOverviewWidgetData {
  callId: string;
  startTime: string;
  caller: string;
  customerName: string;
  companyName: string;
  direction: 'Inbound' | 'Outbound' | 'Internal';
  callStatus: string;
  callQueue: string;
  huntedAgent: string;
  callee: string;
  waitingDuration: string;
  talkingDuration: string;
  callDuration: string;
  participants: string;
}

const getPresenceColor = (presence: string) => {
  switch (presence) {
    case "Answered":
    case "Available":
      return "text-[#26C872] font-semibold";
    case "Missed":
      return "text-[#F44336] font-semibold";
    case "Busy":
      return "text-[#FFC107] font-semibold";
    case "Abandoned":
      return "text-[#6d8596] font-semibold";
    case "Transferred":
      return "text-[#579925] font-semibold";
    case "In A Call":
      return "text-[#2196F3] font-semibold";
    case "In An Internal Call":
      return "text-[#3F51B5] font-semibold";
    case "Offline":
      return "text-primary font-semibold";
    case "Do Not Disturb":
      return "text-[#F44336] font-semibold";
    case "Away":
      return "text-[#FF9800] font-semibold";
    case "Break":
      return "text-[#9E9E9E] font-semibold";
    case "Be Right Back":
      return "text-[#BDBDBD] font-semibold";
    case "Off Work":
      return "text-[#616161] font-semibold";
    case "Present Unknown":
      return "text-[#795548] font-semibold";
    case "Inactive":
      return "text-[#FF5722] font-semibold";
    case "Out Of Office":
      return "text-[#9C27B0] font-semibold";
    default:
      return "";
  }
};

const getDirectionColor = (direction: string) => {
  switch (direction) {
    case "Inbound":
      return "text-[#2196F3] font-semibold";
    case "Inbound Direct":
      return "text-[#166CF6] font-semibold";
    case "Outbound":
      return "text-[#CE93D8] font-semibold";
    case "Internal":
      return "text-[#9FA8DA] font-semibold";
    default:
      return "";
  }
};

const columns = [
  { title: "Call Queue", key: "callQueue", minWidth: 100, maxWidth: 120 },
  { title: "Start Time", key: "startTime", minWidth: 120, maxWidth: 150 },
  { title: "Organizer", key: "caller", minWidth: 100, maxWidth: 130 },
  { title: "Recipient", key: "recipient", minWidth: 100, maxWidth: 130 },
  { title: "Participant", key: "participants", minWidth: 100, maxWidth: 130 },
  { title: "Direction", key: "direction", minWidth: 80, maxWidth: 100 },
  { title: "Call Status", key: "callStatus", minWidth: 90, maxWidth: 110 },
  { title: "Call Duration", key: "callDuration", minWidth: 100, maxWidth: 120 },
  { title: "Voicemail", key: "voicemail", minWidth: 90, maxWidth: 110 }
];

// Columns that are hidden in UI but included in CSV exports
const defaultCsvOnlyColumns = [
  { title: "Waiting Duration", key: "waitingDuration" },
  { title: "Talking Duration", key: "talkingDuration" }
];

// Export to Excel for Call Journey
const exportToExcel = async (
  dateParams: any,
  callDirectionTypes: CallDirectionTypes[] | null,
  filters: any,
  sortKey: string,
  sortDirection: string,
  hiddenColumns: CallsOverviewTableColumns[] = [],
  callMicrosoftIds?: string[],
  csvOnlyColumns: Array<{ title: string; key: string }> = defaultCsvOnlyColumns,
  exportHiddenColumns: CallsOverviewTableColumns[] = [],
  agentDisplayNameTransfer?: string | null
) => {
  try {
    // Note: Subfilters (column filters) are excluded from export
    // Export will include all data without applying column filters

    // Call API to get all data for export (without column filters)
    const exportData = await fetchCallDetailOverviewPaged({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate,
      previousEndDate: dateParams.previousEndDate,
      queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
      agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
      groupIds: dateParams.groupIds ?? undefined,
      page: 1, // Get all data
      limit: null, // Get all data
      callDirectionTypes: callDirectionTypes,
      callMicrosoftIds: callMicrosoftIds,
      agentDisplayNameTransfer: agentDisplayNameTransfer ?? undefined,
      sort: {
        column: sortKey,
        direction: sortDirection as "asc" | "desc"
      },
      filters: undefined // Exclude subfilters from export
    });

    const data = exportData.data || [];

    // Filter columns based on exportHiddenColumns (use exportHiddenColumns if provided, otherwise fallback to hiddenColumns)
    const columnsToHideInExport = exportHiddenColumns.length > 0 ? exportHiddenColumns : hiddenColumns;
    const visibleColumnsForExport = columns.filter(col => !columnsToHideInExport.includes(col.key as CallsOverviewTableColumns));

    // Combine visible columns with CSV-only columns for export
    const allColumnsForExport = [...visibleColumnsForExport, ...csvOnlyColumns];

    const headers = allColumnsForExport.map(col => col.title);

    const rows = data.map(row =>
      allColumnsForExport.map(col => {
        if (col.key === "startTime") {
          return convertLocalTimezone(row[col.key]);
        }
        if (col.key === "voicemail") {
          const v = row[col.key];
          if (v === true) return "Yes";
          if (v === false) return "No";
          return "";
        }
        return row[col.key] || '';
      })
    );

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Column widths - dynamic based on all export columns (visible + CSV-only)
    ws['!cols'] = allColumnsForExport.map(() => ({ wch: 15 }));

    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    XLSX.utils.book_append_sheet(wb, ws, "Call Journey");
    XLSX.writeFile(wb, `calls ${dateStr}.csv`);
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
};

const getUniqueValues = (data, key) => normalizeColumnValues(key, data.map(row => row[key]));

const cloneFilters = (source: Record<string, string[]>) =>
  Object.fromEntries(
    Object.entries(source).map(([key, values]) => [key, Array.isArray(values) ? [...values] : []])
  );

const omitColumnFromFilters = (source: Record<string, string[]>, columnKey: string) => {
  const cloned = cloneFilters(source);
  delete cloned[columnKey];
  return cloned;
};

/** Build filter array for API: voicemail UI values "Yes"/"No" are sent as "true"/"false". */
const filtersToApiFormat = (filters: Record<string, string[]>) =>
  Object.entries(filters)
    .filter(([_, filterValue]) => Array.isArray(filterValue) && filterValue.length > 0)
    .map(([column, filterValue]) => {
      const rawValues = filterValue.map(v => String(v).trim()).filter(Boolean);
      const normalizedValues = normalizeColumnValues(column, filterValue);

      let value: string[];
      if (column === "voicemail") {
        value = normalizedValues.map(v => (v === "Yes" ? "true" : v === "No" ? "false" : v));
      } else if (column === "startTime") {
        // Preserve original timestamp tokens and add UTC-normalized forms to avoid format mismatch
        // when startTime is combined with other column filters.
        value = Array.from(new Set([...rawValues, ...normalizedValues]));
      } else {
        value = normalizedValues;
      }

      return { column, value };
    });

// Debounce hook for search input
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Skeleton loading component for filter items
/* istanbul ignore next: UI-only skeleton markup */
const FilterSkeleton: React.FC = () => (
  <div className="space-y-1">
    {Array.from({ length: 5 }).map((_, idx) => (
      <div key={idx} className="flex items-center gap-2 py-1">
        <div className="h-4 w-4 bg-gray-200 animate-pulse rounded flex-shrink-0" />
        <div className="h-4 bg-gray-200 animate-pulse rounded flex-1 max-w-[160px]" />
      </div>
    ))}
  </div>
);

// Skeleton loading component for pagination
/* istanbul ignore next: UI-only skeleton markup */
const PaginationSkeleton: React.FC = () => (
  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73]">
    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-gray-200 animate-pulse rounded" />
      <div className="h-4 w-16 bg-gray-200 animate-pulse rounded" />
      <div className="w-8 h-8 bg-gray-200 animate-pulse rounded" />
    </div>
  </div>
);

type WidgetColumn = { title: string; key: string; minWidth: number; maxWidth: number };

function resolveVisibleColumns(
  customColumns: WidgetColumn[] | undefined,
  hiddenColumns: CallsOverviewTableColumns[]
): WidgetColumn[] {
  if (customColumns) {
    return customColumns;
  }
  return columns.filter((col) => !hiddenColumns.includes(col.key as CallsOverviewTableColumns));
}

function resolveInsightSection(callDirectionTypes: CallDirectionTypes[] | null): InsightSection {
  return callDirectionTypes?.length === 1 && callDirectionTypes?.[0] === CallDirectionTypes.INBOUND
    ? "CQ_INSIGHT"
    : "DIRECT_INSIGHT";
}

function computeShouldUseClientSideFilterValues(
  requestState: FilterRequestState | null,
  pagedData: PaginatedResponse<CallDetailOverviewRow> | null
): boolean {
  return requestState?.status === "success" && (pagedData?.totalPages ?? 0) <= 1;
}

function buildColumnUniqueValues(
  visibleColumns: Array<{ key: string }>,
  searchFilterData: Record<string, string[]>,
  columnFilterData: CallOverviewColumnFilterItem[],
  pagedRows: CallDetailOverviewRow[]
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  visibleColumns.forEach((col) => {
    if (searchFilterData[col.key] && searchFilterData[col.key].length > 0) {
      result[col.key] = normalizeColumnValues(col.key, searchFilterData[col.key]);
    } else if (columnFilterData.length > 0) {
      const filterItem = columnFilterData.find((item) => item.columnName === col.key);
      if (filterItem?.columnValue?.length > 0) {
        result[col.key] = normalizeColumnValues(col.key, filterItem.columnValue);
      } else {
        result[col.key] = getUniqueValues(pagedRows || [], col.key).map(String);
      }
    } else {
      result[col.key] = getUniqueValues(pagedRows || [], col.key).map(String);
    }

    if (col.key === "voicemail") {
      result[col.key] = ["No", "Yes"];
    }
  });

  return result;
}

function buildHasMoreDataDefaults(
  currentHasMoreData: Record<string, boolean>,
  visibleColumnKeys: string[],
  shouldUseClientSideFilterValues: boolean
): Record<string, boolean> {
  const next = { ...currentHasMoreData };
  visibleColumnKeys.forEach((col) => {
    if (next[col] === undefined) {
      next[col] = !shouldUseClientSideFilterValues;
    }
  });
  return next;
}

function normalizeRequestedPage(page: number, currentPage: number, totalPages: number): number {
  return page > currentPage ? Math.min(totalPages, page) : Math.max(1, page);
}

function computePaginationMeta(
  pagedData: PaginatedResponse<CallDetailOverviewRow> | null,
  currentPage: number,
  itemsPerPage: number
) {
  const totalPages = pagedData?.totalPages > 0 ? pagedData?.totalPages : 0;
  const totalItems = pagedData?.total || (pagedData?.data || []).length;
  const currentPageFromData = pagedData?.totalPages > 0 ? pagedData?.page || currentPage : 0;
  const itemsPerPageFromData = pagedData?.limit || itemsPerPage;
  const startIndex = (currentPageFromData - 1) * itemsPerPageFromData;
  const endIndex = Math.min(startIndex + itemsPerPageFromData, totalItems);

  return {
    totalPages,
    totalItems,
    currentPageFromData,
    itemsPerPageFromData,
    startIndex,
    endIndex,
  };
}


export const __testables = {
  getPresenceColor,
  getDirectionColor,
  getUniqueValues,
  cloneFilters,
  omitColumnFromFilters,
  filtersToApiFormat,
  useDebounce,
  FilterSkeleton,
  PaginationSkeleton,
  resolveVisibleColumns,
  resolveInsightSection,
  computeShouldUseClientSideFilterValues,
  buildColumnUniqueValues,
  buildHasMoreDataDefaults,
  normalizeRequestedPage,
  computePaginationMeta,
};

// Optimized FilterDropdown component
const FilterDropdown: React.FC<{
  colKey: string;
  colTitle: string;
  uniqueValues: string[];
  selected: string[];
  searchValue: string;
  searchFilterData: { [columnName: string]: string[] };
  searchLoading: { [columnName: string]: boolean };
  loadingMore: { [columnName: string]: boolean };
  hasMoreData: { [columnName: string]: boolean };
  limit: number;
  onSearchChange: (value: string) => void;
  onValueToggle: (value: string) => void;
  onClearColumn: () => void;
  onSelectAllColumn: (values: string[]) => void;
  onOpen: () => void;
  onLoadMore: (columnName: string, searchValue?: string) => Promise<void>;
}> = React.memo(({
  colKey,
  colTitle,
  uniqueValues,
  selected,
  searchValue,
  searchFilterData,
  searchLoading,
  loadingMore,
  hasMoreData,
  limit,
  onSearchChange,
  onValueToggle,
  onClearColumn,
  onSelectAllColumn,
  onOpen,
  onLoadMore
}) => {
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const isLoading = searchLoading[colKey] || false;
  const isLoadingMore = loadingMore[colKey] || false;
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [filteredValues, setFilteredValues] = useState<string[]>([]);
  useEffect(() => {
    const normalizedSearch = debouncedSearchValue.toLowerCase();
    const matchesSearch = (value: string) => {
      const raw = String(value);
      if (colKey === "startTime") {
        const localDisplay = formatStartTimeDisplayValue(raw).toLowerCase();
        return raw.toLowerCase().includes(normalizedSearch) || localDisplay.includes(normalizedSearch);
      }
      return raw.toLowerCase().includes(normalizedSearch);
    };
    let visibleValues: string[] = [];
    if (debouncedSearchValue && searchFilterData[colKey] && searchFilterData[colKey].length > 0) {
      visibleValues = searchFilterData[colKey].map(String).filter(matchesSearch);
    } else if (debouncedSearchValue) {
      visibleValues = uniqueValues.filter(matchesSearch);
    } else {
      visibleValues = uniqueValues.length > 0 ? uniqueValues : [];
    }
    let cancelled = false;
    if (debouncedSearchValue && visibleValues.length === 0 && hasMoreData[colKey] === true) {
      loadUntilFound();
    }
    // Always keep selected values visible in the popup list.
    const selectedValues = selected.map(String);
    const allValues = Array.from(new Set([...selectedValues, ...visibleValues]));
    const sortedValues = [...allValues].sort((a, b) => {
      if (colKey === "voicemail") {
        const order = { "No": 0, "Yes": 1 };
        const aOrder = (order as Record<string, number>)[a] ?? 999;
        const bOrder = (order as Record<string, number>)[b] ?? 999;
        return aOrder - bOrder;
      }
      if (colKey === "startTime") {
        return a.localeCompare(b);
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
    setFilteredValues(sortedValues);
    // Loading loop
    async function loadUntilFound() {
      while (debouncedSearchValue && visibleValues.length === 0 && hasMoreData[colKey] === true && !cancelled) {
        await onLoadMore(colKey);
        await new Promise(resolve => setTimeout(resolve, 500));
        if (debouncedSearchValue && searchFilterData[colKey] && searchFilterData[colKey].length > 0) {
          visibleValues = searchFilterData[colKey].map(String).filter(matchesSearch);
        } else if (debouncedSearchValue) {
          visibleValues = uniqueValues.filter(matchesSearch);
        }
      }
    }
    return () => { cancelled = true; };
  }, [uniqueValues, debouncedSearchValue, searchFilterData, colKey, selected, hasMoreData, onLoadMore]);

  // Handle scroll to load more data with debounce
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    const columnHasMoreData = hasMoreData[colKey] === true; // Only true when explicitly set to true

    if (isNearBottom && !isLoadingMore && !isLoading && columnHasMoreData) {
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce the load more call to prevent multiple rapid calls
      scrollTimeoutRef.current = setTimeout(() => {
        // Check if we have search-specific data (meaning we're in search mode)
        if (debouncedSearchValue && searchFilterData[colKey]) {
          onLoadMore(colKey, debouncedSearchValue);
        } else if (!debouncedSearchValue) {
          // Load more general filter data
          onLoadMore(colKey);
        }
      }, 100); // 100ms debounce
    }
  }, [isLoadingMore, isLoading, debouncedSearchValue, colKey, onLoadMore, hasMoreData, searchFilterData]);

  const handleWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  // Get hasMoreData for this column
  const columnHasMoreData = hasMoreData[colKey] === true; // Only true when explicitly set to true

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Ensure wheel events work on scroll container
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheelEvent = (e: WheelEvent) => {
      // Allow wheel scrolling to work normally
      e.stopPropagation();
    };

    scrollContainer.addEventListener('wheel', handleWheelEvent, { passive: true });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  /* istanbul ignore next: UI-only markup */
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild onClick={e => { e.stopPropagation(); onOpen(); }}>
        <Button variant="ghost" size="sm" className="h-6 w-6 min-w-[24px] min-h-[24px] flex-shrink-0" aria-label="Filter column">
          {selected.length > 0 ? (
            <FilterCheckIcon className="h-3 w-3" />
          ) : (
            <FilterIcon className="h-3 w-3" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[220px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-2">
          <div className="mb-2">
            <p className="text-xs font-medium mb-2">Filter by values:</p>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search values..."
                className="flex-1 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {searchValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSearchChange("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <div
            ref={scrollContainerRef}
            className="max-h-[150px] overflow-y-auto pr-2 overscroll-contain"
            onScroll={handleScroll}
            onWheel={handleWheel}
          >
            {isLoading && debouncedSearchValue ? (
              <FilterSkeleton />
            ) : (
              <div className="space-y-1">
                {filteredValues.map((value) => {
                  const displayText =
                    colKey === "startTime" ? formatStartTimeDisplayValue(value) : String(value);
                  return (
                    <div key={String(value)} className="flex min-w-0 items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        id={`${colKey}-${String(value)}`}
                        checked={selected.includes(String(value))}
                        onChange={() => onValueToggle(String(value))}
                        className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                      />
                      <label
                        htmlFor={`${colKey}-${String(value)}`}
                        className="min-w-0 max-w-[160px] cursor-pointer text-xs leading-4"
                      >
                        <TruncatedTextTooltip content={displayText}>{displayText}</TruncatedTextTooltip>
                      </label>
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <div className="h-4 w-4 bg-gray-200 animate-pulse rounded flex-shrink-0" />
                    <span className="text-xs text-gray-500 ml-2">Loading more...</span>
                  </div>
                )}
                {filteredValues?.length > 0 && !debouncedSearchValue && columnHasMoreData && !isLoadingMore && filteredValues.length >= limit && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    Scroll down to load more results {limit}
                  </div>
                )}
                {filteredValues.length > 0 && !columnHasMoreData && !isLoadingMore && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    All results loaded
                  </div>
                )}
                {filteredValues.length === 0 && !isLoading && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between mt-4 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 [text-decoration:none] hover:bg-[#00FEC1] hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black"
              onClick={onClearColumn}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 [text-decoration:none] hover:bg-[#00FEC1] hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black"
              onClick={() => onSelectAllColumn(uniqueValues)}
            >
              Select All
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

const CallsDetailOverviewWidget: React.FC<{
  callDirectionTypes: CallDirectionTypes[] | null;
  limit?: number;
  dateParams: any;
  hiddenColumns?: CallsOverviewTableColumns[];
  initialFilters?: { [key: string]: string[] };
  callMicrosoftIds?: string[];
  exportButtonClassName?: string;
  hideTooltips?: boolean;
  agentName?: string;
  queueName?: string;
  csvOnlyColumns?: Array<{ title: string; key: string }>;
  exportHiddenColumns?: CallsOverviewTableColumns[];
  agentDisplayNameTransfer?: string | null;
  customBadge?: React.ReactNode;
  title?: string;
  customColumns?: Array<{ title: string; key: string; minWidth: number; maxWidth: number }>;
}> = ({ dateParams, callDirectionTypes, limit = 20, hiddenColumns = [], initialFilters = {}, callMicrosoftIds, exportButtonClassName, hideTooltips = false, agentName, queueName, csvOnlyColumns = defaultCsvOnlyColumns, exportHiddenColumns = [], agentDisplayNameTransfer, customBadge, title = "Call Journey", customColumns }) => {
  const visibleColumns = useMemo(() => {
    return resolveVisibleColumns(customColumns, hiddenColumns);
  }, [hiddenColumns, customColumns]);
  const [sortKey, setSortKey] = useState("startTime");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filters, setFilters] = useState(initialFilters);
  const [searchValues, setSearchValues] = useState({});
  const [searchLoading, setSearchLoading] = useState<{ [columnName: string]: boolean }>({});
  const [columnFilterData, setColumnFilterData] = useState<CallOverviewColumnFilterItem[]>([]);
  const [searchFilterData, setSearchFilterData] = useState<{ [columnName: string]: string[] }>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallsDetailOverviewWidgetData | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  // State for tracking loaded pages for each column filter
  const [loadedPages, setLoadedPages] = useState<{ [columnName: string]: number }>({});
  const [loadingMore, setLoadingMore] = useState<{ [columnName: string]: boolean }>({});
  const [hasMoreData, setHasMoreData] = useState<{ [columnName: string]: boolean }>({});
  // Call Detail Overview Paged
  const [pagedData, setPagedData] = React.useState<PaginatedResponse<CallDetailOverviewRow> | null>({
    data: [],
    total: 0,
    page: 1,
    limit: limit,
    totalPages: 1
  });
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isRefreshingData, setIsRefreshingData] = React.useState<boolean>(false);
  const [hasRequestError, setHasRequestError] = React.useState<boolean>(false);
  const [requestState, setRequestState] = React.useState<FilterRequestState | null>(null);
  const [diagnosticsMetric, setDiagnosticsMetric] = React.useState<FilterDiagnosticsMetric>({
    invalidStartTimeExcludedCount: 0,
    filterLatencyMs: 0,
    section:
      callDirectionTypes?.length === 1 && callDirectionTypes?.[0] === CallDirectionTypes.INBOUND
        ? "CQ_INSIGHT"
        : "DIRECT_INSIGHT",
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(limit); // Display items per page
  // Track if filters have been cleared to force refresh
  const [filtersCleared, setFiltersCleared] = useState(false);

  // Abort controller for overview requests (paged list)
  const overviewAbortRef = React.useRef<AbortController | null>(null);
  const requestSequenceRef = React.useRef(0);
  const activeRequestIdRef = React.useRef<string | null>(null);

  const insightSection: InsightSection = resolveInsightSection(callDirectionTypes);

  const toEmptyPagedData = useCallback(
    (page = 1): PaginatedResponse<CallDetailOverviewRow> => ({
      data: [],
      total: 0,
      page,
      limit: itemsPerPage,
      totalPages: 1,
    }),
    [itemsPerPage]
  );

  const beginTrackedRequest = useCallback((criteriaHash: string) => {
    const requestId = `filter-${Date.now()}-${++requestSequenceRef.current}`;
    activeRequestIdRef.current = requestId;
    setRequestState({
      requestId,
      criteriaHash,
      status: "pending",
    });
    setHasRequestError(false);
    return requestId;
  }, []);

  const isStaleRequest = useCallback((requestId: string, signal: AbortSignal) => {
    if (signal.aborted) return true;
    return activeRequestIdRef.current !== requestId;
  }, []);

  // Reset current page and collapse all expanded rows when data changes (filter/refresh)
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, [pagedData?.data]);

  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
      setCurrentPage(1);
      setExpandedRows(new Set());
    }
  }, [initialFilters]);

  const shouldUseClientSideFilterValues = computeShouldUseClientSideFilterValues(requestState, pagedData);

  // Memoize unique values for each column to avoid recalculation
  const columnUniqueValues = useMemo(() => {
    return buildColumnUniqueValues(
      visibleColumns,
      searchFilterData,
      columnFilterData,
      pagedData?.data || []
    );
  }, [visibleColumns, columnFilterData, searchFilterData, pagedData?.data]);

  const ensureColumnFilterData = useCallback(async (columnName: string) => {
    const appliedFilters = omitColumnFromFilters(filters, columnName);

    try {
      const filterData = await fetchCallDetailOverviewColumnFilter({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        previousStartDate: dateParams.previousStartDate,
        previousEndDate: dateParams.previousEndDate,
        queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
        callDirectionTypes: callDirectionTypes,
        groupIds: dateParams.groupIds ?? undefined,
        callMicrosoftIds: callMicrosoftIds,
        page: 1,
        limit: limit,
        filterColumns: [{
          columnName,
          searchValue: null,
        }],
        appliedFilters,
      });

      const columnData = filterData.find(item => item.columnName === columnName)?.columnValue || [];

      setColumnFilterData(prev => {
        const rest = prev.filter(item => item.columnName !== columnName);
        return [...rest, { columnName, columnValue: columnData }];
      });

      setLoadedPages(prev => ({
        ...prev,
        [columnName]: columnData.length > 0 ? 1 : 0,
      }));

      setHasMoreData(prev => ({
        ...prev,
        [columnName]: columnData.length >= limit,
      }));
    } catch (error) {
      console.error(`Error fetching initial filter data for column ${columnName}:`, error);
    }
  }, [
    filters,
    dateParams,
    callDirectionTypes,
    callMicrosoftIds,
    limit,
  ]);

  const handleSort = (key) => {
    let newSortDirection = "asc";
    if (sortKey === key) {
      newSortDirection = sortDirection === "asc" ? "desc" : "asc";
    }

    setSortKey(key);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page when sorting

    // Remove manual API call - let useEffect handle it
  };

  // Function to load more filter data for a specific column
  const loadMoreFilterData = useCallback(async (columnName: string, searchValue?: string) => {
    const currentPage = loadedPages[columnName] || 1;
    const nextPage = currentPage + 1;

    // Prevent loading if already loading or no more data
    if (loadingMore[columnName] || hasMoreData[columnName] === false) {
      return;
    }

    // Set loading state
    setLoadingMore(prev => ({
      ...prev,
      [columnName]: true
    }));

    try {
      const filterData = await fetchCallDetailOverviewColumnFilter({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        previousStartDate: dateParams.previousStartDate,
        previousEndDate: dateParams.previousEndDate,
        queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
        callDirectionTypes: callDirectionTypes,
        groupIds: dateParams.groupIds ?? undefined,
        callMicrosoftIds: callMicrosoftIds,
        page: nextPage,
        limit: limit,
        filterColumns: [{
          columnName: columnName,
          searchValue: searchValue || null
        }],
        appliedFilters: omitColumnFromFilters(filters, columnName)
      });

      // Find the data for the specific column we're loading more
      const columnData = filterData.find(item => item.columnName === columnName);
      if (columnData && columnData.columnValue && columnData.columnValue.length > 0) {
        // Get existing data to avoid duplicates
        const existingSearchData = searchFilterData[columnName] || [];
        const existingColumnData = columnFilterData.find(item => item.columnName === columnName);
        const existingColumnValues = existingColumnData?.columnValue || [];

        // Combine existing data from both sources
        const allExistingData = [...new Set([...existingSearchData, ...existingColumnValues])];
        const existingSet = new Set(allExistingData);

        // Filter out duplicates
        const newUniqueData = columnData.columnValue.filter(value => !existingSet.has(value));

        if (newUniqueData.length > 0) {
          // Update searchFilterData if we're in search mode
          if (searchValue) {
            setSearchFilterData(prev => ({
              ...prev,
              [columnName]: [...existingSearchData, ...newUniqueData]
            }));
          } else {
            // Update columnFilterData for general filter data
            setColumnFilterData(prev => {
              const updatedData = prev.map(item => {
                if (item.columnName === columnName) {
                  return {
                    ...item,
                    columnValue: [...(item.columnValue || []), ...newUniqueData]
                  };
                }
                return item;
              });

              // If column doesn't exist in columnFilterData, add it
              if (!updatedData.find(item => item.columnName === columnName)) {
                updatedData.push({
                  columnName,
                  columnValue: newUniqueData
                });
              }

              return updatedData;
            });
          }
        }

        // Update loaded page count
        setLoadedPages(prev => ({
          ...prev,
          [columnName]: nextPage
        }));

        // Check if we got less data than expected (indicating no more data)
        if (columnData.columnValue.length < limit) {
          setHasMoreData(prev => ({
            ...prev,
            [columnName]: false
          }));
        }
      } else {
        // No more data available
        setHasMoreData(prev => ({
          ...prev,
          [columnName]: false
        }));
      }
    } catch (error) {
      console.error(`Error loading more filter data for column ${columnName}:`, error);

      // Set hasMoreData to false on error to prevent further attempts
      setHasMoreData(prev => ({
        ...prev,
        [columnName]: false
      }));
    } finally {
      // Clear loading state
      setLoadingMore(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
    }
  }, [dateParams, callDirectionTypes, limit, loadedPages, loadingMore, searchFilterData, columnFilterData, hasMoreData, callMicrosoftIds, filters]);

  // Function to fetch filtered data for a specific column with search value
  const fetchColumnFilterWithSearch = useCallback(async (columnName: string, searchValue: string) => {
    if (!searchValue || searchValue.length === 0) {
      // Clear search-specific data for this column
      setSearchFilterData(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
      setSearchLoading(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
      setLoadedPages(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
      setHasMoreData(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
      return;
    }

    // Set loading state
    setSearchLoading(prev => ({
      ...prev,
      [columnName]: true
    }));

    try {
      const filterData = await fetchCallDetailOverviewColumnFilter({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        previousStartDate: dateParams.previousStartDate,
        previousEndDate: dateParams.previousEndDate,
        queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
        callDirectionTypes: callDirectionTypes,
        groupIds: dateParams.groupIds ?? undefined,
        callMicrosoftIds: callMicrosoftIds,
        page: 1,
        limit: limit,
        filterColumns: [{
          columnName: columnName,
          searchValue: searchValue
        }],
        appliedFilters: omitColumnFromFilters(filters, columnName)
      });

      // Store the search-specific filter data for this column
      // Find the data for the specific column we're searching
      const columnData = filterData.find(item => item.columnName === columnName);
      if (columnData && columnData.columnValue && columnData.columnValue.length > 0) {
        setSearchFilterData(prev => ({
          ...prev,
          [columnName]: columnData.columnValue
        }));

        // Reset loaded page count for new search
        setLoadedPages(prev => ({
          ...prev,
          [columnName]: 1
        }));

        // Reset hasMoreData for new search
        setHasMoreData(prev => ({
          ...prev,
          [columnName]: true
        }));
      } else {
        // API returned empty data, clear search-specific data to fallback to table data
        setSearchFilterData(prev => {
          const newData = { ...prev };
          delete newData[columnName];
          return newData;
        });
        setLoadedPages(prev => {
          const newData = { ...prev };
          delete newData[columnName];
          return newData;
        });
        setHasMoreData(prev => {
          const newData = { ...prev };
          delete newData[columnName];
          return newData;
        });
      }
    } catch (error) {
      console.error(`Error fetching filter data for column ${columnName}:`, error);

      // Clear search-specific data on error to fallback to table data
      setSearchFilterData(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
    } finally {
      // Clear loading state
      setSearchLoading(prev => {
        const newData = { ...prev };
        delete newData[columnName];
        return newData;
      });
    }
  }, [dateParams, callDirectionTypes, limit, callMicrosoftIds, filters]);

  // Store timeout references for cleanup
  const searchTimeouts = React.useRef<{ [key: string]: NodeJS.Timeout }>({});

  const handleSearchChange = (key, value) => {
    setSearchValues((prev) => ({ ...prev, [key]: value }));

    // Clear previous timeout for this column
    if (searchTimeouts.current[key]) {
      clearTimeout(searchTimeouts.current[key]);
    }

    // Start Time is displayed in converted local format, so keep search client-side for accurate matching.
    if (key === "startTime") {
      setSearchLoading(prev => {
        const newData = { ...prev };
        delete newData[key];
        return newData;
      });
      setSearchFilterData(prev => {
        const newData = { ...prev };
        delete newData[key];
        return newData;
      });
      return;
    }

    // Set new timeout for API call
    searchTimeouts.current[key] = setTimeout(() => {
      fetchColumnFilterWithSearch(key, value);
    }, 500);
  };

  const clearFilters = () => {
    setFilters({});
    setExpandedRows(new Set());
    setSearchValues({});
    setSearchLoading({});
    setSearchFilterData({});
    setLoadedPages({});
    setLoadingMore({});
    setCurrentPage(1); // Reset to first page when clearing filters
    setFiltersCleared(true); // Set flag to force refresh

    // Reinitialize hasMoreData for all visible columns
    const visibleColumnKeys = visibleColumns.map(col => col.key);
    const initialHasMoreData = {};
    visibleColumnKeys.forEach(col => {
      initialHasMoreData[col] = true;
    });
    setHasMoreData(initialHasMoreData);
  };

  const handleValueToggle = (colKey, value) => {
    setExpandedRows(new Set());
    setFilters((prev) => {
      const arr = prev[colKey] || [];
      const newFilters = arr.includes(value)
        ? { ...prev, [colKey]: arr.filter((v) => v !== value) }
        : { ...prev, [colKey]: [...arr, value] };

      // Remove manual API call - let useEffect handle it

      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when toggling filter values
  };

  const handleClearColumn = (colKey) => {
    setExpandedRows(new Set());
    setFilters((prev) => {
      const newFilters = { ...prev, [colKey]: [] };

      // Remove manual API call - let useEffect handle it

      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when clearing column
  };

  const handleSelectAllColumn = (colKey, values) => {
    setExpandedRows(new Set());
    setFilters((prev) => {
      const newFilters = { ...prev, [colKey]: values };

      // Remove manual API call - let useEffect handle it

      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when selecting all
  };

  const toggleRow = (index: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleShowDetails = (row: CallsDetailOverviewWidgetData) => {
    setSelectedCall(row);
    setModalOpen(true);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await exportToExcel(dateParams, callDirectionTypes, filters, sortKey, sortDirection, hiddenColumns, callMicrosoftIds, csvOnlyColumns, exportHiddenColumns, agentDisplayNameTransfer);
    } catch (error) {
      console.error('Export failed:', error);
      // You can add toast notification here if needed
    } finally {
      setExportLoading(false);
    }
  };

  const runOverviewRequest = useCallback((params: {
    page: number;
    limit: number;
    sort?: { column: string; direction: "asc" | "desc" };
    filters?: Array<{ column: string; value: string[] }>;
    uiFiltersSnapshot: Record<string, string[]>;
    setRefreshing?: boolean;
    criteriaHash: string;
    errorContext: "page change" | "refresh";
  }) => {
    if (overviewAbortRef.current) {
      overviewAbortRef.current.abort();
    }
    const controller = new AbortController();
    overviewAbortRef.current = controller;

    setLoading(true);
    if (params.setRefreshing) {
      setIsRefreshingData(true);
    }

    const requestId = beginTrackedRequest(params.criteriaHash);
    const requestStartedAt = performance.now();

    fetchCallDetailOverviewPaged({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate,
      previousEndDate: dateParams.previousEndDate,
      queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
      agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
      groupIds: dateParams.groupIds ?? undefined,
      page: params.page,
      limit: params.limit,
      callDirectionTypes: callDirectionTypes,
      callMicrosoftIds: callMicrosoftIds,
      agentDisplayNameTransfer: agentDisplayNameTransfer ?? undefined,
      sort: params.sort,
      filters: params.filters,
    }, { signal: controller.signal })
      .then((data) => {
        if (isStaleRequest(requestId, controller.signal)) {
          setRequestState((prev) =>
            prev?.requestId === requestId
              ? {
                ...prev,
                status: "superseded",
              }
              : prev
          );
          return;
        }

        const { validRows, excludedCount } = filterRowsWithValidStartTime(data.data || []);
        const normalizedTotal = Math.max(0, (data.total ?? 0) - excludedCount);

        const nextPagedData = {
          ...data,
          data: validRows,
          total: normalizedTotal,
          totalPages: data.limit ? Math.max(1, Math.ceil(normalizedTotal / data.limit)) : data.totalPages,
        };

        setPagedData(nextPagedData);
        setDiagnosticsMetric({
          section: insightSection,
          invalidStartTimeExcludedCount: excludedCount,
          filterLatencyMs: Math.round(performance.now() - requestStartedAt),
        });
        setRequestState((prev) =>
          prev?.requestId === requestId
            ? {
              ...prev,
              status: "success",
            }
            : prev
        );
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.name === "AbortError") {
          setRequestState((prev) =>
            prev?.requestId === requestId
              ? {
                ...prev,
                status: "aborted",
              }
              : prev
          );
          return;
        }
        console.error(`Error fetching call overview (${params.errorContext}):`, err);
        if (!isStaleRequest(requestId, controller.signal)) {
          setPagedData(toEmptyPagedData(params.page ?? 1));
          setHasRequestError(true);
          setRequestState((prev) =>
            prev?.requestId === requestId
              ? {
                ...prev,
                status: "error",
              }
              : prev
          );
        }
      })
      .finally(() => {
        if (!isStaleRequest(requestId, controller.signal)) {
          setLoading(false);
          if (params.setRefreshing) {
            setIsRefreshingData(false);
          }
        }
      });
  }, [
    dateParams,
    callDirectionTypes,
    callMicrosoftIds,
    agentDisplayNameTransfer,
    beginTrackedRequest,
    isStaleRequest,
    toEmptyPagedData,
    insightSection,
  ]);

  const processPageChange = (page: number) => {
    page = normalizeRequestedPage(page, currentPage, totalPages);
    setCurrentPage(page);
    setExpandedRows(new Set());

    const filterArray = filtersToApiFormat(filters);

    const criteriaHash = JSON.stringify({
      page,
      sortKey,
      sortDirection,
      filters,
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
    });

    runOverviewRequest({
      page,
      limit: itemsPerPage,
      sort: {
        column: sortKey,
        direction: sortDirection as "asc" | "desc"
      },
      filters: filterArray,
      uiFiltersSnapshot: cloneFilters(filters),
      criteriaHash,
      errorContext: "page change",
    });
  };

  const refreshCallDetailOverview = useCallback(({ page, limit, sort = undefined, filters = undefined, uiFiltersSnapshot = {} }) => {
    const criteriaHash = JSON.stringify({
      page,
      limit,
      sort,
      filters,
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate,
      previousEndDate: dateParams.previousEndDate,
    });
    runOverviewRequest({
      page,
      limit,
      sort,
      filters,
      uiFiltersSnapshot,
      setRefreshing: true,
      criteriaHash,
      errorContext: "refresh",
    });
  }, [
    dateParams,
    runOverviewRequest,
  ]);

  // Effect for when dateParams change - reset filter data but keep filter values
  useEffect(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;

    // Reset only filter data states, but keep current filter values
    setColumnFilterData([]);
    setLoadedPages({});
    setLoadingMore({});
    setHasMoreData({});
    setSearchFilterData({});
    setSearchLoading({});
    // Keep filters state - don't reset it

    // Prepare current filters for API call
    const filterArray = filtersToApiFormat(filters);

    refreshCallDetailOverview({
      page: 1,
      limit: limit,
      sort: {
        column: sortKey,
        direction: sortDirection as "asc" | "desc"
      },
      filters: filterArray.length > 0 ? filterArray : undefined,
      uiFiltersSnapshot: cloneFilters(filters),
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams, callMicrosoftIds]);

  useEffect(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;

    const visibleColumnKeys = visibleColumns.map(col => col.key);

    // Keep existing popup option state stable while users are selecting values.
    // Only initialize per-column hasMore defaults when missing.
    setHasMoreData(prev => {
      return buildHasMoreDataDefaults(prev, visibleColumnKeys, shouldUseClientSideFilterValues);
    });

    return undefined;
  }, [
    dateParams,
    callDirectionTypes,
    callMicrosoftIds,
    visibleColumns,
    limit,
    shouldUseClientSideFilterValues,
  ]);

  // Effect for when filters or sort change - refresh data with current filters
  useEffect(() => {
    // Only skip if we don't have dateParams (initial load)
    if (!dateParams?.startDate || !dateParams?.endDate) return;

    // Set loading state immediately when API call starts
    setLoading(true);
    setIsRefreshingData(true);

    const filterArray = filtersToApiFormat(filters);

    refreshCallDetailOverview({
      page: 1,
      limit: limit,
      sort: {
        column: sortKey,
        direction: sortDirection as "asc" | "desc"
      },
      filters: filterArray.length > 0 ? filterArray : undefined,
      uiFiltersSnapshot: cloneFilters(filters),
    });

    // Reset the filtersCleared flag after refresh
    if (filtersCleared) {
      setFiltersCleared(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortKey, sortDirection, filtersCleared]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = searchTimeouts.current;
    return () => {
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      if (overviewAbortRef.current) {
        overviewAbortRef.current.abort();
      }
    };
  }, []);

  // Pagination calculations
  const { totalPages, totalItems, currentPageFromData, itemsPerPageFromData, startIndex, endIndex } =
    computePaginationMeta(pagedData, currentPage, itemsPerPage);

  // Use pagedData directly since API handles pagination
  const paginatedData = pagedData?.data || [];

  /* istanbul ignore next: UI-only markup */
  return (
    <Card className="w-full overflow-hidden h-[600px] max-h-[calc(100vh-6rem)] flex flex-col dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b rounded-t-lg flex-shrink-0">
        <div className="flex items-center gap-2">
          <span>{title}</span>
          {customBadge || <AgentQueueBadge agentName={agentName} queueName={queueName} />}
          {!hideTooltips && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="cursor-pointer">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="max-w-xs text-xs font-normal">
                    The timeline of a call from the initial attempt to reach the callee to the moment the call is terminated within the given time.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2">
          {Object.values(filters).some((v) => Array.isArray(v) && v.length > 0) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs transition-opacity duration-200"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "text-xs flex items-center gap-1 [text-decoration:none] hover:bg-[#00FEC1] hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black",
              exportButtonClassName
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleExport();
            }}
            disabled={exportLoading || loading}
          >
            <Download className="h-3 w-3" />
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        {hasRequestError ? (
          <div className="flex-1 min-h-0 flex items-center justify-center px-6 py-10">
            <div className="w-full max-w-xl rounded-lg border border-red-200 bg-red-50 dark:bg-[#3C1D1D] dark:border-red-800 p-6 text-center space-y-3">
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-300">Unable to refresh Call Journey</h3>
              <p className="text-sm text-red-600 dark:text-red-200">
                Filtered results could not be loaded. The table was cleared to avoid showing stale data.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const filterArray = filtersToApiFormat(filters);
                  refreshCallDetailOverview({
                    page: 1,
                    limit,
                    sort: {
                      column: sortKey,
                      direction: sortDirection as "asc" | "desc",
                    },
                    filters: filterArray.length > 0 ? filterArray : undefined,
                  });
                }}
              >
                Retry
              </Button>
              <p className="text-[11px] text-red-500 dark:text-red-200/80">
                {requestState?.status === "error"
                  ? `Last request: ${requestState.requestId}`
                  : ""}
              </p>
            </div>
          </div>
        ) : null}
        {!hasRequestError ? (
          <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden flex flex-col">
            <div className="w-full flex-1 min-h-0 overflow-y-auto overflow-x-auto">
              <div className="min-w-full inline-block" style={loading ? { minHeight: `${45 + itemsPerPageFromData * 45}px` } : undefined}>
                {/* Table using CSS table display */}
                <div className="table w-full call-journey-table" style={{ borderCollapse: 'collapse', borderSpacing: 0 }}>
                  {/* Header Row */}
                  <div className="table-row bg-gray-50 text-xs font-medium text-teamscx-primary border-b border-t border-gray-300 dark:bg-[#3C5C73] dark:text-white dark:border-white sticky top-0 z-50">
                    <div className="table-cell h-[45px] px-1 text-center transition-colors align-middle sticky left-0 z-40 bg-gray-50 dark:bg-[#3C5C73] font-bold border-b dark:border-t border-l-0 border-r-0 border-gray-300 dark:border-white" style={{ width: 32 }}>
                      <div className="flex items-center justify-center">
                        <span className="w-4"></span>
                      </div>
                    </div>
                    {visibleColumns.map((col, colIdx) => {
                      const uniqueValues: string[] = columnUniqueValues[col.key] || [];
                      const search = searchValues[col.key] || "";
                      const selected: string[] = Array.isArray(filters[col.key]) ? filters[col.key] : [];

                      return (
                        <div
                          key={col.key}
                          className={cn(
                            "table-cell h-[45px] px-2 text-center transition-colors border-gray-300 dark:border-white align-middle",
                            "border-b dark:border-t border-l-0 border-r-0 border-gray-300 dark:border-white"
                          )}
                          style={{
                            minWidth: col.minWidth,
                            maxWidth: col.maxWidth,
                            width: `${100 / visibleColumns.length}%`
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <div
                              className="flex items-center gap-1 cursor-pointer"
                              onClick={() => handleSort(col.key)}
                            >
                              {col.title}
                              {sortKey === col.key && (
                                sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                              )}
                            </div>
                            <FilterDropdown
                              colKey={col.key}
                              colTitle={col.title}
                              uniqueValues={uniqueValues}
                              selected={selected}
                              searchValue={search}
                              searchFilterData={searchFilterData}
                              searchLoading={searchLoading}
                              loadingMore={loadingMore}
                              hasMoreData={hasMoreData}
                              limit={limit}
                              onSearchChange={(value) => handleSearchChange(col.key, value)}
                              onValueToggle={(value) => handleValueToggle(col.key, value)}
                              onClearColumn={() => handleClearColumn(col.key)}
                              onSelectAllColumn={(values) => handleSelectAllColumn(col.key, values)}
                              onOpen={() => ensureColumnFilterData(col.key)}
                              onLoadMore={(columnName, searchValue) => loadMoreFilterData(columnName, searchValue)}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {/* Hide actions column for ledger phase */}
                    {/* <div className="table-cell h-[45px] px-2 py-3 text-center transition-colors border-gray-300 dark:border-white align-middle border-b dark:border-t border-gray-300 dark:border-white" style={{ width: 80 }}>
                  Actions
                </div> */}
                  </div>

                  {/* Data Rows */}
                  <div className="table-row-group border-t border-gray-300 dark:border-white">
                    {loading
                      ? Array.from({ length: itemsPerPageFromData }).map((_, idx) => (
                        <div key={idx} className={cn("table-row", idx % 2 === 0 ? "bg-white" : "bg-gray-50", "dark:bg-[#0C3451]")}>
                          <div className="table-cell h-[45px] px-1 py-3 border-gray-300 dark:border-white align-middle sticky left-0 z-20 font-semibold border-l-0 border-r-0" style={{ width: 32 }}>
                            <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                          </div>
                          {visibleColumns.map((col, colIdx) => (
                            <div
                              key={col.key}
                              className={cn(
                                "table-cell h-[45px] px-2 py-3 border-gray-300 dark:border-white align-middle border-l-0 border-r-0",
                              )}
                              style={{
                                minWidth: col.minWidth,
                                maxWidth: col.maxWidth,
                                width: `${100 / visibleColumns.length}%`
                              }}
                            >
                              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                            </div>
                          ))}
                          {/* <div className="table-cell h-[45px] px-2 py-3 border-gray-300 dark:border-white align-middle border-l-0 border-r-0" style={{ width: 80 }}>
                        <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                      </div> */}
                        </div>
                      ))
                      : paginatedData.length === 0 ? (
                        <tr className="table-row">
                          <td className="table-cell text-center align-middle py-12 text-muted-foreground dark:text-white" colSpan={visibleColumns.length + 2}>
                            <p className="text-sm">No call records were found</p>
                          </td>
                        </tr>
                      ) : (
                        paginatedData.map((row, idx) => (
                          <React.Fragment key={idx}>
                            <div
                              className={cn(
                                "table-row transition-colors border-b border-gray-300 dark:border-white",
                                idx % 2 === 0 ? "bg-white dark:bg-[#0C3451]" : "bg-gray-50 dark:bg-[#3C5C73]"
                              )}
                            >
                              <div className="table-cell h-[45px] px-1 border-b border-l-0 border-r-0 border-gray-300 dark:border-white align-middle sticky left-0 z-20 font-semibold" style={{ width: 32 }}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-primary/90 hover:text-white transition-all duration-200 flex items-center justify-center"
                                  onClick={() => toggleRow(idx)}
                                >
                                  {expandedRows.has(idx) ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                              {visibleColumns.map((col, colIdx) => (
                                <div
                                  key={col.key}
                                  className={cn(
                                    "table-cell h-[45px] px-2 text-xs text-teamscx-primary dark:text-white border-gray-300 dark:border-white transition-colors align-middle border-b border-l-0 border-r-0",
                                  )}
                                  style={{
                                    minWidth: col.minWidth,
                                    maxWidth: col.maxWidth,
                                    width: `${100 / visibleColumns.length}%`
                                  }}
                                >
                                  <div className="td-cell">
                                    {(col.key === "callQueue" || col.key === "participants" || col.key === "recipient") ? (
                                      <TruncatedTextTooltip
                                        content={String(row[col.key] ?? "-")}
                                      >
                                        {String(row[col.key] ?? "-")}
                                      </TruncatedTextTooltip>
                                    ) : (
                                      <p className="truncate">
                                        {col.key === "callStatus" ? (
                                          <span className={getPresenceColor(row[col.key])}>{row[col.key]}</span>
                                        ) : col.key === "startTime" ? (
                                          convertLocalTimezone(row[col.key])
                                        ) : col.key === "direction" ? (
                                          <span className={getDirectionColor(row[col.key])}>{row[col.key]}</span>
                                        ) : col.key === "voicemail" ? (
                                          <span className="flex w-full pl-8">
                                            <Checkbox
                                              checked={row[col.key] === true}
                                              tabIndex={-1}
                                              className="h-5 w-5 rounded-md border-2 border-gray-300 dark:border-white/40 bg-transparent data-[state=checked]:bg-[#00FEC1] data-[state=checked]:border-[#00FEC1] data-[state=checked]:text-black pointer-events-none cursor-default"
                                              aria-label={row[col.key] === true ? "Call forwarded to voice mail" : "Not forwarded to voice mail"}
                                            />
                                          </span>
                                        ) : (
                                          String(row[col.key] ?? "-")
                                        )}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {/* Hide actions column for ledger phase 1 */}
                              {/* <div className="table-cell h-[45px] px-2 border-b border-gray-300 dark:border-white align-middle text-center" style={{ width: 80 }}>
                            <button
                              className="border-2 text-blue-900 bg-white rounded-lg px-2 py-1 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-blue-50 transition text-xs"
                              onClick={() => handleShowDetails(row)}
                            >
                              Details
                            </button>
                          </div> */}
                            </div>
                            {expandedRows.has(idx) && (
                              <tr className="table-row">
                                <td className="table-cell text-center align-middle py-2 text-muted-foreground bg-white dark:bg-[#3C5C73] dark:text-white" colSpan={visibleColumns.length + 2}>
                                  <Timeline callId={row.callId} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="hidden" aria-hidden="true" data-testid="call-journey-diagnostics">
          {JSON.stringify(diagnosticsMetric)}
        </div>

        {/* Pagination Controls - Show skeleton only when refreshing data, not when paginating */}
        {loading || isRefreshingData ? (
          <PaginationSkeleton />
        ) : (
          <div className="flex flex-shrink-0 items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73]">
            <div className="text-xs text-muted-foreground">
              Showing {totalItems > 0 ? startIndex + 1 : 0}-{endIndex} of {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => processPageChange(currentPageFromData - 1)}
                disabled={currentPageFromData <= 1 || loading}
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs px-1">{currentPageFromData} / {totalPages}</span>
              <button
                className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => processPageChange(currentPageFromData + 1)}
                disabled={currentPageFromData >= totalPages || loading}
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
      <CallDetailsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        callDetail={selectedCall}
      />
    </Card>
  );
};

/* istanbul ignore next: module export */
export default CallsDetailOverviewWidget;
